/**
 * Cart drawer with quantity controls.
 *
 * Open/close + body lock + escape + focus trap come from Drawer.
 * This subclass adds: cart:open / cart:refresh event listeners,
 * quantity-button click delegation, Section Rendering API refresh,
 * an AJAX-saved order note, and automatic product recommendations
 * (Product Recommendations API) for the upsell list, ordered and preselected
 * against the size already in the cart.
 */
import { Drawer } from "@theme/drawer";

// Shared markup with hhs-icon.liquid, inlined for JS-rendered upsell cards.
// bag-plus = the "add to cart" affordance; check = the added confirmation.
const ICON_BAG_PLUS = `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 8 H19 L18 21 H6 Z"/><path d="M9 8 V6 a3 3 0 0 1 6 0 v2"/><line x1="12" y1="12.5" x2="12" y2="17.5"/><line x1="9.5" y1="15" x2="14.5" y2="15"/></svg>`;
const ICON_CHECK = `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="5 12 10 17 19 7"/></svg>`;

// Option names that mean "size" across the shop's two locales; the shopper's
// size drives which upsell products and variants are offered.
const SIZE_OPTION_NAMES = ["veličina", "velicina", "size"];

class CartDrawer extends Drawer {
  /** Set when a quantity change came back capped; consumed by refresh(). */
  #stockNotice = null;

  setup() {
    super.setup();

    document.addEventListener("cart:open", () => this.open());
    document.addEventListener("cart:refresh", () => this.refresh());

    this.#bindQuantityButtons();
    this.#bindVariantChange();
    this.#bindUpsellForms();
    this.#bindUpsellSwatches();
    this.#bindUpsellSizeSelect();
    this.#bindNote();
    this.#refreshRecommendations();

    new MutationObserver(() => {
      if (!this.panel.classList.contains("is-open")) {
        document.documentElement.classList.remove("cart-is-open");
      }
    }).observe(this.panel, { attributes: true, attributeFilter: ["class"] });
  }

  #bindNote() {
    // Save the order note to the Shopify cart on change/blur, debounced.
    let timer = null;
    const save = (value) => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        fetch("/cart/update.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ note: value }),
        }).catch((err) => console.error("Cart note save error:", err));
      }, 400);
    };
    this.addEventListener("input", (e) => {
      const field = e.target.closest("[data-cart-note]");
      if (field) save(field.value);
    });
    this.addEventListener(
      "blur",
      (e) => {
        const field = e.target.closest("[data-cart-note]");
        if (field) {
          clearTimeout(timer);
          fetch("/cart/update.js", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ note: field.value }),
          }).catch((err) => console.error("Cart note save error:", err));
        }
      },
      true,
    );
  }

  #bindUpsellForms() {
    this.addEventListener("submit", async (e) => {
      const form = e.target.closest("[data-upsell-form]");
      if (!form) return;
      e.preventDefault();
      const btn = form.querySelector("[data-upsell-add]");
      const original = btn?.innerHTML;
      if (btn) {
        btn.disabled = true;
        btn.classList.add("is-added");
        btn.innerHTML = ICON_CHECK;
      }
      const id = form.querySelector("[name=id]")?.value;
      try {
        await fetch("/cart/add.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id, quantity: 1 }),
        });
        await this.refresh();
        document.dispatchEvent(new CustomEvent("cart:updated"));
      } catch (err) {
        console.error("Upsell add error:", err);
        if (btn) {
          btn.disabled = false;
          btn.classList.remove("is-added");
          btn.innerHTML = original || ICON_BAG_PLUS;
        }
      }
    });
  }

  /**
   * Clicking a color swatch inside an upsell card swaps that card in place to
   * the chosen sibling product (image, title, price, add-to-cart variant)
   * instead of navigating to its product page. All the data needed lives on
   * the swatch itself (data-swatch-*), so no extra request is made. Delegated
   * so it also covers swatches injected after the initial render.
   */
  #bindUpsellSwatches() {
    this.addEventListener("click", (e) => {
      const swatch = e.target.closest("[data-swatch]");
      if (!swatch || !this.contains(swatch)) return;
      e.preventDefault();
      // Dimmed = this colour has nothing in the shopper's size.
      if (swatch.classList.contains("is-unavailable")) return;
      this.#selectUpsellSwatch(swatch);
    });
  }

  #selectUpsellSwatch(swatch) {
    const form = swatch.closest("[data-upsell-form]");
    if (!form) return;
    const d = swatch.dataset;

    const input = form.querySelector("input[name=id]");

    const imageLink = form.querySelector("[data-upsell-image]");
    if (imageLink) {
      if (d.swatchUrl) imageLink.setAttribute("href", d.swatchUrl);
      const img = imageLink.querySelector("img");
      if (img && d.swatchImage) {
        img.removeAttribute("srcset");
        img.src = d.swatchImage;
        if (d.swatchTitle) img.alt = d.swatchTitle;
      }
    }

    const titleLink = form.querySelector("[data-upsell-title]");
    if (titleLink) {
      if (d.swatchUrl) titleLink.setAttribute("href", d.swatchUrl);
      if (d.swatchTitle) titleLink.textContent = d.swatchTitle;
    }

    // Repoint the size picker at the new colour's stock, keeping the size the
    // shopper had picked when this colour carries it too. #applyUpsellSize then
    // syncs the add button, the price and the rest of the strip; without a
    // picker (colours not sold by size) the swatch's own data is all there is.
    const select = form.querySelector("[data-upsell-size-select]");
    const sizes = this.#parseSwatchSizes(swatch);
    const priceEl = form.querySelector("[data-upsell-price]");
    if (select && sizes && Object.keys(sizes).length) {
      const wanted = select.selectedOptions?.[0]?.dataset.sizeKey;
      select.innerHTML = this.#sizeOptions(
        Object.entries(sizes).map(([key, size]) => ({
          id: size.id,
          key,
          label: size.label,
          price: size.price,
        })),
        wanted,
      );
      this.#applyUpsellSize(select);
    } else {
      if (input && d.swatchVariant) input.value = d.swatchVariant;
      if (priceEl && d.swatchPrice) priceEl.textContent = d.swatchPrice;
    }

    // Move the selected-color ring to the clicked swatch.
    const strip = swatch.closest(".hhs-upsell-swatches");
    if (strip) {
      strip.querySelectorAll("[data-swatch]").forEach((el) => {
        const active = el === swatch;
        el.classList.toggle("is-active", active);
        if (active) el.setAttribute("aria-current", "true");
        else el.removeAttribute("aria-current");
      });
    }
  }

  /**
   * Replace the upsell list with automatic recommendations keyed off the
   * first cart line item, excluding products already in the cart. Falls
   * back to the server-rendered list (manual collection) on empty/error.
   */
  async #refreshRecommendations() {
    const container = this.$("[data-cart-upsell]");
    if (!container) return;

    const targetId = container.dataset.upsellTarget;
    if (!targetId) {
      // Empty cart — keep whatever (fallback) markup the server rendered.
      this.#toggleUpsell(container);
      return;
    }

    const inCart = (container.dataset.upsellInCart || "")
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    const sizeKey = (container.dataset.upsellSize || "").trim().toLowerCase();

    try {
      // Ten rather than three: the list is sorted by size availability below,
      // so it needs more candidates than it shows to find matches at all.
      const res = await fetch(
        `/recommendations/products.json?product_id=${encodeURIComponent(
          targetId,
        )}&intent=related&limit=10`,
      );
      if (!res.ok) throw new Error("recommendations request failed");
      const data = await res.json();
      const products = (data.products || []).filter(
        (p) => p.available !== false && !inCart.includes(String(p.id)),
      );

      // Products stocked in the shopper's size come first, with that size
      // preselected on the picker; the rest top the list up to three so an
      // uncommon size never empties the section out.
      const cards = products.map((product) => ({
        product,
        sizes: this.#upsellSizes(product, sizeKey),
      }));
      const ordered = [
        ...cards.filter((c) => c.sizes?.matched),
        ...cards.filter((c) => !c.sizes?.matched),
      ].slice(0, 3);

      if (ordered.length > 0) {
        const addLabel = container.dataset.upsellAddLabel || "ADD";
        const list = container.querySelector("[data-upsell-list]");
        if (list) {
          list.innerHTML = ordered
            .map((c) => this.#renderUpsellCard(c.product, addLabel, c.sizes))
            .join("");
          this.#loadUpsellSwatches(list);
        }
      }
      // else: leave the server-rendered fallback list in place.
    } catch (err) {
      console.error("Recommendations error:", err);
      // Keep the fallback list.
    }

    // Server-rendered strips (fallback collection) need the same size wiring;
    // the ones fetched above get it as they land, in #loadUpsellSwatches.
    container
      .querySelectorAll(".hhs-upsell-swatches")
      .forEach((strip) =>
        this.#applySwatchSizes(strip, this.#cardSizeKey(strip)),
      );

    this.#toggleUpsell(container);
  }

  /**
   * The sizes `product` has in stock, plus which one the card should open on:
   * the cart's size when it's there (`matched`, which also floats the card up
   * the list), otherwise the first one available. null when the product isn't
   * sold by size, or is sold out of every size.
   *
   * Option names arrive as plain strings from the recommendations JSON, but as
   * objects from other product endpoints — both shapes are handled so the same
   * helper keeps working if the source ever changes.
   */
  #upsellSizes(product, sizeKey) {
    const names = (product.options || []).map((o) =>
      typeof o === "string" ? o : o?.name,
    );
    const idx = names.findIndex((n) =>
      SIZE_OPTION_NAMES.includes(
        String(n || "")
          .trim()
          .toLowerCase(),
      ),
    );
    if (idx < 0) return null;

    const sizes = (product.variants || [])
      .filter((v) => v.available)
      .map((v) => ({
        id: String(v.id),
        label: String(v.options?.[idx] ?? ""),
        key: String(v.options?.[idx] ?? "")
          .trim()
          .toLowerCase(),
        price: this.#formatMoney(v.price),
      }));
    if (!sizes.length) return null;

    const match = sizeKey ? sizes.find((size) => size.key === sizeKey) : null;
    return {
      name: names[idx],
      sizes,
      selected: match || sizes[0],
      matched: !!match,
    };
  }

  /**
   * `<option>` markup for a size picker. `wantedKey` keeps the shopper's own
   * size selected across a colour swap whenever the new colour carries it.
   */
  #sizeOptions(sizes, wantedKey) {
    const template =
      this.$("[data-cart-upsell]")?.dataset.upsellSizeLabel || "__SIZE__";
    const selected =
      sizes.find((size) => size.key === wantedKey) || sizes[0] || null;
    return sizes
      .map(
        (size) => `
          <option
            value="${this.#escape(size.id)}"
            data-size-key="${this.#escape(size.key)}"
            data-price="${this.#escape(size.price)}"
            ${size === selected ? "selected" : ""}
          >${this.#escape(template.replace("__SIZE__", size.label))}</option>`,
      )
      .join("");
  }

  /** Point the add button at the size the shopper picked on an upsell card. */
  #bindUpsellSizeSelect() {
    this.addEventListener("change", (e) => {
      const select = e.target.closest("[data-upsell-size-select]");
      if (select) this.#applyUpsellSize(select);
    });
  }

  #applyUpsellSize(select) {
    const form = select.closest("[data-upsell-form]");
    if (!form) return;
    const option = select.selectedOptions?.[0];

    const input = form.querySelector("input[name=id]");
    if (input && select.value) input.value = select.value;

    const priceEl = form.querySelector("[data-upsell-price]");
    if (priceEl && option?.dataset.price) {
      priceEl.textContent = option.dataset.price;
    }

    // The colours that can't be had in the size just picked go dim.
    const strip = form.querySelector(".hhs-upsell-swatches");
    if (strip) this.#applySwatchSizes(strip, option?.dataset.sizeKey || "");
  }

  /**
   * The size a card is currently set to — the picker's own selection, falling
   * back to the cart's size for cards that have no picker.
   */
  #cardSizeKey(el) {
    const form = el?.closest?.("[data-upsell-form]");
    const option = form?.querySelector("[data-upsell-size-select]")
      ?.selectedOptions?.[0];
    if (option?.dataset.sizeKey) return option.dataset.sizeKey;
    return (this.$("[data-cart-upsell]")?.dataset.upsellSize || "")
      .trim()
      .toLowerCase();
  }

  /** A swatch's size map, or null when the colour isn't sold by size. */
  #parseSwatchSizes(swatch) {
    const raw = swatch?.dataset.swatchSizes;
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }

  /**
   * Point every colour swatch at the shopper's size and dim the colours that
   * don't stock it, so switching colour can't quietly change the size on the
   * add button. With no size in the cart (or no size map on the swatch) the
   * strip is left exactly as Liquid rendered it.
   */
  #applySwatchSizes(strip, sizeKey) {
    if (!strip || !sizeKey) return;
    strip.querySelectorAll("[data-swatch]").forEach((swatch) => {
      // No map = this colour isn't sold by size, so leave it alone. An empty
      // map = it is, but none of it is in stock.
      const sizes = this.#parseSwatchSizes(swatch);
      if (!sizes) return;
      const match = sizes[sizeKey];
      if (!match) {
        swatch.classList.add("is-unavailable");
        swatch.setAttribute("aria-disabled", "true");
        return;
      }
      swatch.dataset.swatchVariant = match.id;
      if (match.price) swatch.dataset.swatchPrice = match.price;
      if (match.label) swatch.dataset.swatchSizeLabel = match.label;
      swatch.classList.remove("is-unavailable");
      swatch.removeAttribute("aria-disabled");
    });
  }

  /** Show the upsell section only when it has at least one card. */
  #toggleUpsell(container) {
    const hasCards = !!container.querySelector("[data-upsell-form]");
    if (hasCards) container.removeAttribute("hidden");
    else container.setAttribute("hidden", "");
  }

  #renderUpsellCard(product, addLabel, sizes) {
    const selected = sizes?.selected;
    const variantId =
      selected?.id ||
      product.variants?.find((v) => v.available)?.id ||
      product.variants?.[0]?.id;
    const price =
      selected?.price ??
      this.#formatMoney(
        product.price_min ?? product.price ?? product.variants?.[0]?.price,
      );
    const sizePicker = sizes
      ? `<select
            class="hhs-cart__size-select hhs-cart__size-select--sm mt-1.5"
            data-upsell-size-select
            aria-label="${this.#escape(sizes.name ?? "")}"
          >${this.#sizeOptions(sizes.sizes, selected?.key)}</select>`
      : "";
    const img = product.featured_image || product.images?.[0] || "";
    const handle =
      product.handle ||
      (product.url || "").split("/products/")[1]?.split(/[?#]/)[0] ||
      "";
    const imgTag = img
      ? `<img src="${this.#escape(img)}" alt="${this.#escape(
          product.title,
        )}" loading="lazy" class="w-full h-full object-cover">`
      : "";

    return `
      <form
        action="/cart/add"
        method="post"
        class="grid grid-cols-[80px_1fr_auto] gap-3 items-center py-3 m-0 border-b border-ink/8 last-of-type:border-b-0"
        data-upsell-form
      >
        <input type="hidden" name="id" value="${this.#escape(variantId)}">
        <a href="${this.#escape(
          product.url,
        )}" data-upsell-image class="block w-20 h-20 bg-bone overflow-hidden border-0" style="border-radius:10px;">${imgTag}</a>
        <div class="min-w-0">
          <a
            href="${this.#escape(product.url)}"
            data-upsell-title
            class="block font-body text-[12px] font-semibold tracking-micro uppercase text-ink leading-[1.3] no-underline border-0"
          >${this.#escape(product.title)}</a>
          <div class="font-mono text-[12px] text-fg-muted mt-0.5" data-upsell-price>${price}</div>
          ${sizePicker}
          <div data-upsell-swatches data-handle="${this.#escape(handle)}"></div>
        </div>
        <button
          type="submit"
          class="hhs-cart__upsell-add grid place-items-center w-9 h-9 bg-transparent border-0 text-ink cursor-pointer transition-colors duration-(--dur-fast) ease-pop hover:text-hophop-pink"
          data-upsell-add
          aria-label="${this.#escape(addLabel)}"
        >${ICON_BAG_PLUS}</button>
      </form>`;
  }

  /**
   * Change a cart line's size in place via its dropdown. Shopify's AJAX API
   * can't swap a line's variant, so we add the chosen variant and drop the old
   * line (it lands at the bottom). The line index stays valid because add()
   * appends.
   */
  #bindVariantChange() {
    this.addEventListener("change", (e) => {
      const select = e.target.closest("[data-variant-select]");
      if (!select) return;
      this.#showItemLoading(select.closest("li"), "Ažuriranje…");
      const lineKey = select.dataset.lineKey;
      const qty = parseInt(select.dataset.qty, 10) || 1;
      this.#changeVariant(lineKey, qty, select.value);
    });
  }

  async #changeVariant(lineKey, quantity, variantId) {
    try {
      // Shopify's AJAX API can't swap a line's variant, and add() always drops
      // the new line at the top. To keep the item in place we snapshot the
      // cart, then rebuild it in the same order with the one line swapped.
      const cart = await (await fetch("/cart.js")).json();
      const items = cart.items || [];
      const desired = items.map((it) => {
        const line = {
          id: it.key === lineKey ? variantId : it.variant_id,
          quantity: it.key === lineKey ? quantity : it.quantity,
        };
        if (it.properties && Object.keys(it.properties).length) {
          line.properties = it.properties;
        }
        const sellingPlan = it.selling_plan_allocation?.selling_plan?.id;
        if (sellingPlan) line.selling_plan = sellingPlan;
        return line;
      });

      await fetch("/cart/clear.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
      // add() prepends, so re-add bottom-up to restore the original order.
      for (let i = desired.length - 1; i >= 0; i--) {
        await fetch("/cart/add.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(desired[i]),
        });
      }
      await this.refresh();
      document.dispatchEvent(new CustomEvent("cart:updated"));
    } catch (err) {
      console.error("Variant change error:", err);
      await this.refresh();
    }
  }

  /**
   * The recommendations JSON carries no metafields, so color_group swatches
   * can't be read client-side. For each JS-rendered upsell card we fetch the
   * product's swatch strip via the Section Rendering API (Liquid context, which
   * *does* have metafields) and inject it. Empty responses (no color group)
   * leave the card untouched. Runs concurrently; failures are silent.
   */
  #loadUpsellSwatches(list) {
    const slots = list.querySelectorAll(
      "[data-upsell-swatches][data-handle]:not([data-swatches-loaded])",
    );
    slots.forEach(async (slot) => {
      slot.setAttribute("data-swatches-loaded", "");
      const handle = slot.dataset.handle;
      if (!handle) return;
      try {
        const res = await fetch(
          `/products/${encodeURIComponent(handle)}?section_id=cart-upsell-swatches`,
        );
        if (!res.ok) return;
        const html = await res.text();
        // The Section Rendering API wraps output in a #shopify-section-* div;
        // pull out just the swatch strip.
        const fragment = document.createRange().createContextualFragment(html);
        const strip = fragment.querySelector(".hhs-upsell-swatches");
        if (strip) {
          slot.replaceWith(strip);
          this.#applySwatchSizes(strip, this.#cardSizeKey(strip));
        }
      } catch (err) {
        console.error("Upsell swatches error:", err);
      }
    });
  }

  /** Prices from the recommendations JSON are in cents. */
  #formatMoney(cents) {
    if (cents == null) return "";
    const amount = (Number(cents) / 100).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    const fmt = window.Shopify?.currency?.active
      ? `${amount} ${window.Shopify.currency.active}`
      : amount;
    return fmt;
  }

  #escape(value) {
    return String(value ?? "").replace(
      /[&<>"']/g,
      (c) =>
        ({
          "&": "&amp;",
          "<": "&lt;",
          ">": "&gt;",
          '"': "&quot;",
          "'": "&#39;",
        })[c],
    );
  }

  open() {
    super.open();
    document.documentElement.classList.add("cart-is-open");
    this.refresh();
  }

  close() {
    super.close();
    document.documentElement.classList.remove("cart-is-open");
  }

  #bindQuantityButtons() {
    this.addEventListener("click", (e) => {
      const btn = e.target.closest("[data-change-qty]");
      if (!btn) return;
      const line = parseInt(btn.dataset.line, 10);
      const qty = parseInt(btn.dataset.qty, 10);
      this.#showItemLoading(
        btn.closest("li"),
        qty === 0 ? "Uklanjanje…" : "Ažuriranje…",
      );
      this.#updateQuantity(line, qty);
    });
  }

  /**
   * Overlay the cart line being changed with a spinner + message, so the user
   * sees which item is updating. The overlay lives on the old DOM and is
   * discarded when refresh() swaps in fresh markup.
   */
  #showItemLoading(item, message) {
    if (!item || item.querySelector(".hhs-cart-item-loading")) return;
    const overlay = document.createElement("div");
    overlay.className = "hhs-cart-item-loading";
    overlay.setAttribute("role", "status");
    overlay.setAttribute("aria-live", "polite");
    overlay.innerHTML =
      `<span class="hhs-cart-item-loading__spinner" aria-hidden="true"></span>` +
      `<span>${this.#escape(message)}</span>`;
    item.appendChild(overlay);
  }

  async #updateQuantity(line, quantity) {
    try {
      const response = await fetch("/cart/change.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ line, quantity }),
      });

      // Running out of stock reaches us two different ways depending on the
      // shop: some refuse the change with a 422, others answer 200 and quietly
      // cap the line at what's left. Both look identical to the shopper — the
      // number springs back — so both have to be caught and explained.
      if (!response.ok) {
        if (quantity > 0) {
          // The stepper only ever asks for one more than the line holds, so a
          // refusal puts the ceiling at quantity - 1. That's the fallback when
          // the cart can't be re-read — better a right number from arithmetic
          // than no explanation at all.
          const available = await this.#lineQuantity(line);
          this.#stockNotice = {
            line,
            available: available === null ? quantity - 1 : available,
          };
        }
        await this.refresh();
        return;
      }

      const cart = await response.json();

      const changed = quantity > 0 ? cart.items[line - 1] : null;
      this.#stockNotice =
        changed && changed.quantity < quantity
          ? { line, available: changed.quantity }
          : null;

      document.dispatchEvent(
        new CustomEvent("cart:updated", {
          detail: { item_count: cart.item_count },
        }),
      );

      const countEl = this.$("[data-cart-header-count]");
      if (countEl) countEl.textContent = `(${cart.item_count})`;

      const status = document.getElementById("cart-status");
      if (status) {
        status.textContent =
          quantity === 0
            ? "Item removed from cart."
            : `Cart updated. ${cart.item_count} ${cart.item_count === 1 ? "item" : "items"} in cart.`;
      }

      await this.refresh();
    } catch (error) {
      console.error("Cart update error:", error);
      await this.refresh();
    }
  }

  /**
   * How many of a line the cart actually holds right now. Used after a refused
   * change, where the error body names the product but never the number.
   * @returns {Promise<number|null>} null if the cart can't be read.
   */
  async #lineQuantity(line) {
    try {
      const response = await fetch("/cart.js");
      if (!response.ok) return null;
      const cart = await response.json();
      return cart.items[line - 1]?.quantity ?? null;
    } catch {
      return null;
    }
  }

  async refresh() {
    try {
      const response = await fetch(
        `${window.location.pathname}?sections=cart-drawer`,
      );
      const data = await response.json();
      const html = data["cart-drawer"];

      if (html) {
        const fragment = document.createRange().createContextualFragment(html);
        const newContent = fragment.querySelector("[data-cart-drawer-content]");
        const currentContent = this.$("[data-cart-drawer-content]");
        if (newContent && currentContent) {
          currentContent.replaceWith(newContent);
        }
        // The free shipping tracker sits between the header and the body, so
        // neither swap above reaches it — without this it kept the totals from
        // page load. It also appears and disappears with the first/last item,
        // hence all three cases.
        const TRACKER = "[data-free-shipping-tracker]";
        const newTracker = fragment.querySelector(TRACKER);
        const currentTracker = this.$(TRACKER);
        if (newTracker && currentTracker) {
          currentTracker.replaceWith(newTracker);
        } else if (newTracker && !currentTracker) {
          const body = this.$("[data-cart-drawer-content]");
          if (body) body.before(newTracker);
        } else if (!newTracker && currentTracker) {
          currentTracker.remove();
        }

        const newFooter = fragment.querySelector("[data-cart-drawer-footer]");
        const currentFooter = this.$("[data-cart-drawer-footer]");
        if (newFooter && currentFooter) {
          currentFooter.replaceWith(newFooter);
        } else if (newFooter && !currentFooter) {
          this.panel.appendChild(newFooter);
        } else if (!newFooter && currentFooter) {
          currentFooter.remove();
        }

        const newCount = fragment.querySelector("[data-cart-header-count]");
        const currentCount = this.$("[data-cart-header-count]");
        if (newCount && currentCount) {
          currentCount.textContent = newCount.textContent;
        }
      }

      this.#renderStockNotice();

      // Cart contents changed — re-fetch recommendations against the new
      // first line item and re-exclude in-cart products.
      this.#refreshRecommendations();
    } catch (error) {
      console.error("Failed to refresh cart drawer:", error);
    }
  }

  /**
   * Print "only N left in stock" on the line that just hit its cap. Runs after
   * the refresh, since the swap above replaces the markup it attaches to, and
   * clears itself so a later refresh doesn't repeat a stale message.
   */
  #renderStockNotice() {
    const notice = this.#stockNotice;
    this.#stockNotice = null;
    if (!notice) return;

    const item = this.$(`[data-cart-line="${notice.line}"]`);
    const template = this.#stockLimitText(notice.available);
    if (!item || !template) return;

    const el = document.createElement("p");
    el.className = "hhs-cart-stock-notice";
    el.setAttribute("role", "status");
    el.textContent = template.replace("__COUNT__", notice.available);
    item.appendChild(el);
  }

  /**
   * The stock message in the plural form the count calls for. Intl does the
   * choosing against the page locale, so Serbian gets "1 komad" but
   * "2 komada" without that rule being written into the theme; locales with
   * no `few` category simply never ask for it.
   */
  #stockLimitText(count) {
    const forms = {
      one: this.dataset.stockLimitOne,
      few: this.dataset.stockLimitFew,
      other: this.dataset.stockLimitOther,
    };
    let category = "other";
    try {
      const locale = document.documentElement.lang || "en";
      category = new Intl.PluralRules(locale).select(count);
    } catch {
      // Unknown locale tag — `other` is the safe form in every language.
    }
    return forms[category] || forms.other;
  }
}

customElements.define("cart-drawer", CartDrawer);

export { CartDrawer };
